import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { FC, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postGeoAdd } from "tm-shared/api";
import { WorkStatus } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useQueueInfo } from "@/hooks";
import { Queue } from "@/hooks/core/useAsyncTaskQueue";
import { useAppSelector, useOfflineModeSelector } from "@/hooks/redux";
import BaseButton from "@/shared/ui/BaseButton";
import { FloatingActionButton } from "@/shared/ui/FloatingActionButton";
import { getPreciseLocation } from "@/utils/geo";

import { useWorkPlan } from "../../WorkPlan.context";
import { useEndWorkFlow } from "./useEndWorkFlow";
import { useStartWorkFlow } from "./useStartWorkFlow";

interface WorkControlsBlockProps {
    queue: Queue;
    openDayStartedModal: () => void;
    openForceStopWorkModal: () => void;
}
export const WorkControlsBlock: FC<WorkControlsBlockProps> = ({
    queue,
    openDayStartedModal,
    openForceStopWorkModal,
}) => {
    const navigate = useNavigate();
    const { capabilities } = useWorkPlan();

    // User/work state
    const workStatus = useAppSelector((s) => s.work.workStatus);

    // UI/offline state
    const offlineMode = useOfflineModeSelector();
    const activeCollapse = useAppSelector((s) => s.ui.workPlan.activeCollapse);
    const isUnscheduledPointsProcessing = useAppSelector(
        (s) => s.offline.isUnscheduledPointsProcessing,
    );

    // Local state
    const hasShownReminder = useRef<{ task: boolean; checkout: boolean }>({
        task: false,
        checkout: false,
    });

    // Async helpers
    const { hasPending } = useQueueInfo();

    const sendLocation = useCallback(async () => {
        if (offlineMode) return;

        const locationTime = dayjs().toISOString();

        try {
            const { accuracy, lat, lng } = await getPreciseLocation();

            await postGeoAdd({
                accuracy,
                locationTime,
                latitude: lat,
                longitude: lng,
            });
        } catch (error) {
            console.error(error);
        }
    }, [offlineMode]);

    const [handleStartWork, isProcessingStartWork] = useStartWorkFlow({
        capabilities,
        queue,
        onOpenDayStartedModal: openDayStartedModal,
        onSendLocation: sendLocation,
    });

    const [handleEndWork, isProcessingEndWork] = useEndWorkFlow({
        hasShownReminder,
        onSendLocation: sendLocation,
        onForceStopRequired: openForceStopWorkModal,
    });

    const canShowAddUnplannedButton =
        !offlineMode && !isUnscheduledPointsProcessing && !activeCollapse;
    const isManagerControls = capabilities.canUseManagerWorkControls;
    const isStartedWorkDay = workStatus === WorkStatus.StartWork;
    const isEndedWorkDay = workStatus === WorkStatus.EndWork;
    const canShowEndWorkButton = !activeCollapse && !offlineMode;

    const handleNavigateToUnplannedPoint = useCallback(() => {
        navigate(ROUTES.WorkPlanUnplannedPoint);
    }, [navigate]);

    // Manager view
    if (isManagerControls) {
        return canShowAddUnplannedButton ? (
            <FloatingActionButton
                onClick={handleNavigateToUnplannedPoint}
                icon={<PlusOutlined />}
                position="fixed"
                right={10}
                bottom={70}
                zIndex={1}
            />
        ) : null;
    }

    // Regular user: active work day
    if (isStartedWorkDay) {
        return (
            <>
                {canShowAddUnplannedButton && (
                    <FloatingActionButton
                        onClick={handleNavigateToUnplannedPoint}
                        icon={<PlusOutlined />}
                        position="fixed"
                        right={10}
                        bottom={140}
                        zIndex={1}
                    />
                )}

                {canShowEndWorkButton && (
                    <BaseButton
                        type="default"
                        danger
                        onClick={handleEndWork}
                        loading={isProcessingEndWork}
                        disabled={hasPending}
                    >
                        Завершити день
                    </BaseButton>
                )}
            </>
        );
    }

    // Regular user: work day not started
    if (isEndedWorkDay) {
        return (
            <BaseButton
                bgColor="success"
                type="primary"
                onClick={handleStartWork}
                loading={isProcessingStartWork}
                disabled={offlineMode}
            >
                Почати
            </BaseButton>
        );
    }

    return null;
};
